import { lazy, Suspense } from 'react';
import { ToastProvider, GlobalErrorBoundary, ToastContainer, LoginForm, AuthGuard, ThemeProvider } from '@textile-erp/ui';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuthContext } from './providers/AuthProvider';
import { ServiceProvider } from './providers/ServiceProvider';
import { MenuProvider } from './providers/MenuProvider';
import { AppLayout } from './app/layout/AppLayout';
import { ModuleRouter } from './app/routing/ModuleRouter';
import { MenuGuard } from './components/MenuGuard';

import '@textile-erp/ui/dist/styles/theme.css';

// Parking full-page screens load on demand, so the parking code stays out of the main bundle
const CheckInCreatePage = lazy(() => import('@textile-erp/parking-management/features/check-in/pages/CheckInCreatePage').then((m) => ({ default: m.CheckInCreatePage })));
const WalkInCheckInCreatePage = lazy(() => import('@textile-erp/parking-management/features/check-in/pages/WalkInCheckInCreatePage').then((m) => ({ default: m.WalkInCheckInCreatePage })));
const ParcelCheckInCreatePage = lazy(() => import('@textile-erp/parking-management/features/check-in/pages/ParcelCheckInCreatePage').then((m) => ({ default: m.ParcelCheckInCreatePage })));
const SelfEntryCheckInPage = lazy(() => import('@textile-erp/parking-management/features/check-in/pages/SelfEntryCheckInPage').then((m) => ({ default: m.SelfEntryCheckInPage })));
const ManualEntryCountPage = lazy(() => import('@textile-erp/parking-management/features/check-in/pages/ManualEntryCountPage').then((m) => ({ default: m.ManualEntryCountPage })));
const LiveStatusReportPage = lazy(() => import('@textile-erp/parking-management/features/reports/live-status-report/pages/LiveStatusReportPage').then((m) => ({ default: m.LiveStatusReportPage })));



// App Routes Component
function AppRoutes() {
  const { user, login, loginByStaffCode, isLoading, error } = useAuthContext();

  // Create stable developmentDefaults object
  // Create stable developmentDefaults object
  // const developmentDefaults = useMemo(() => {
  //   const defaults = {
  //     email: "admin@akahamed.com",
  //     password: "admin123"
  //   };

  //   return defaults;
  // }, []);
  const developmentDefaults = null as any;

  return (
    <Suspense fallback={<div className="min-h-screen bg-theme-secondary" />}>
    <Routes>
      {/* Public routes (no authentication required) */}
      <Route path="/parking/self-entry/:gateId" element={<SelfEntryCheckInPage />} />

      {/* Protected routes (authentication required) */}
      <Route path="/*" element={
        <AuthGuard
          isAuthenticated={!!user}
          isLoading={isLoading}
          fallback={
            <div className="min-h-screen bg-theme-secondary flex items-center justify-center">
              <LoginForm
                onLogin={async (credentials) => {
                  await login(credentials);
                }}
                onLoginByStaffCode={async (credentials) => {
                  await loginByStaffCode(credentials);
                }}
                loading={isLoading}
                error={error || undefined}
                title="Sign in to AK Ahamed"
                description="Enter your credentials to access the system"
                developmentDefaults={developmentDefaults}
              />
            </div>
          }
        >
          <Routes>
            {/* Full-page routes without AppLayout (sidebar) - still need the menu access, like sidebar pages */}
            <Route path="/parking/check-ins/create" element={<MenuGuard><CheckInCreatePage /></MenuGuard>} />
            <Route path="/parking/walk-ins/create" element={<MenuGuard><WalkInCheckInCreatePage /></MenuGuard>} />
            <Route path="/parking/parcels/create" element={<MenuGuard><ParcelCheckInCreatePage /></MenuGuard>} />
            <Route path="/parking/manual-entry" element={<MenuGuard><ManualEntryCountPage /></MenuGuard>} />
            <Route path="/parking/reports/live-status" element={<MenuGuard><LiveStatusReportPage /></MenuGuard>} />

            {/* Regular routes with AppLayout */}
            <Route path="/*" element={
              <AppLayout>
                <ModuleRouter />
              </AppLayout>
            } />
          </Routes>
        </AuthGuard>
      } />
    </Routes>
    </Suspense>
  );
}

function App() {

  return (
    <GlobalErrorBoundary
      enableErrorTracking={true}
      enablePerformanceMonitoring={true}
      enableAutoReporting={true}
      maxRetries={3}
      retryDelay={2000}
      onError={(error, errorInfo) => {
        // Custom error handling logic can go here
        console.log('App-level error handler triggered:', error, errorInfo);
      }}
    >
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <MenuProvider>
              <ServiceProvider>
                <Router>
                  <AppRoutes />
                  <ToastContainer />
                </Router>
              </ServiceProvider>
            </MenuProvider>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </GlobalErrorBoundary>
  );
}

export default App;
